import _memoize from 'lodash/memoize';
import { getFont, FONT } from '@tornado-rdk/typography';
import { FULL_HD, HD, RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import SkeletonRectangle from '../../skeleton/skeleton.rectangle.component';
import { cacheResolver, makeDefaultStylingContext } from '../../utils/style';
// NOTE: primary and secondary CTA share the same height per customer, so a single dimension fits every button
import {
  HEIGHT as CTA_HEIGHT,
  BORDER_RADIUS as CTA_BORDER_RADIUS,
} from '../../callToAction/primary/callToAction.constants';
import {
  templates,
  sizes,
  SHOW_SERVICE_ICON,
  SHOW_HIGHLIGHT,
  SHOW_EXTRA_HIGHLIGHT,
  SHOW_DETAILS,
  SHOW_CONTENT_ADVISORY,
  SHOW_BOTTOM_ROW,
  EXTRA_TITLE_FONT,
  EXTRA_TITLE_FONT_TYPE,
  DESCRIPTION_FONT,
  CTA_MARGIN_BOTTOM,
  CTA_BUTTON_MARGIN_RIGHT,
} from '../jumbotron.constants';
import {
  LOGO_DIMENSIONS,
  FLEX_CONTAINER_WIDTH,
  FLEX_CONTAINER_MARGIN_BOTTOM,
  LOGO_MARGIN_BOTTOM,
  SERVICE_PICTO_DIMENSIONS,
  SERVICE_PICTO_MARGIN_BOTTOM,
  HIGHLIGHT_MARGIN_BOTTOM,
  EXTRA_TITLE_MARGIN_BOTTOM,
  EXTRA_HIGHLIGHT_MARGIN_BOTTOM,
  EXTRA_HIGHLIGHT_MARGIN_RIGHT,
  DESCRIPTION_MARGIN_BOTTOM,
  DETAILS_MARGIN_BOTTOM,
  CONTENT_ADVISORY_ICON_DIMENSION,
  BASIC_PICTO_DIMENSION,
  BOTTOM_ROW_GAP,
  makeBasicTextStyle,
} from '../jumbotron.style';

export const defaultContext = makeDefaultStylingContext();

/**
 * NOTE: real text and pictos are rendered with their own transparent paddings, while skeleton rectangles are not,
 * so an explicit gap is required to keep stacked placeholders readable as separate entries
 */
export const RECTANGLE_GAP = {
  [HD]: 4,
  [FULL_HD]: 8,
};

// NOTE: placeholders imitate an average content length, as the real one is unknown before the data is loaded
export const TEXT_WIDTH_RATIO = {
  HIGHLIGHT: 0.4,
  EXTRA_TITLE: 0.85,
  EXTRA_HIGHLIGHT: 0.55,
  DESCRIPTION: 1,
  DESCRIPTION_LAST_LINE: 0.7,
  DETAILS: 0.5,
  EXTRA_DETAILS: 0.35,
};

// NOTE: widths of the most common set of CTAs: play, my list and information
export const BUTTON_WIDTHS = {
  [HD]: [152, 180, 98],
  [FULL_HD]: [200, 240, 142],
};

export const CONTENT_ADVISORY_ICONS_COUNT = 3;

export const getInitialStyle = _memoize(({ screenSize = FULL_HD } = defaultContext) => {
  const { w, h } = sizes[templates.Jumbotron][RENDERING_CONTEXT.GENERAL][screenSize];
  const textWidth = FLEX_CONTAINER_WIDTH[screenSize];
  const gap = RECTANGLE_GAP[screenSize];
  const getTextRectangleWidth = ratio => Math.round(textWidth * ratio);

  const highlightFont = getFont(FONT.BERLIN_1, screenSize);
  const extraTitleFont = getFont(EXTRA_TITLE_FONT, screenSize, EXTRA_TITLE_FONT_TYPE);
  const descriptionFont = getFont(DESCRIPTION_FONT, screenSize);
  const detailsFont = getFont(FONT.BERLIN_2, screenSize);
  const extraDetailsFont = getFont(FONT.OTTAWA_2, screenSize);

  // NOTE: the amount of description placeholders follows the amount of lines the real description is wrapped into
  const descriptionLines = makeBasicTextStyle({ font: descriptionFont }).text.maxLines;
  const advisoryIconSize = CONTENT_ADVISORY_ICON_DIMENSION[screenSize].h;
  const basicIconSize = BASIC_PICTO_DIMENSION[screenSize];
  const isContentRatingIconVisible = !SHOW_CONTENT_ADVISORY && !SHOW_BOTTOM_ROW;
  const isExtraHighlightRowVisible = SHOW_EXTRA_HIGHLIGHT || SHOW_CONTENT_ADVISORY || isContentRatingIconVisible;

  return {
    FlexContainer: {
      w,
      h: h - FLEX_CONTAINER_MARGIN_BOTTOM[screenSize],
      flex: {
        direction: 'column',
        justifyContent: 'flex-end',
      },
      Logo: {
        type: SkeletonRectangle,
        w: LOGO_DIMENSIONS[screenSize].width,
        h: LOGO_DIMENSIONS[screenSize].height,
        flexItem: {
          marginBottom: LOGO_MARGIN_BOTTOM[screenSize],
        },
      },
      ServiceIcon: {
        type: SkeletonRectangle,
        visible: SHOW_SERVICE_ICON,
        w: SERVICE_PICTO_DIMENSIONS[screenSize].width,
        h: SERVICE_PICTO_DIMENSIONS[screenSize].height,
        flexItem: {
          marginBottom: SERVICE_PICTO_MARGIN_BOTTOM[screenSize],
        },
      },
      MetadataRow: {
        flex: {
          direction: 'column',
        },
        Highlight: {
          type: SkeletonRectangle,
          visible: SHOW_HIGHLIGHT,
          w: getTextRectangleWidth(TEXT_WIDTH_RATIO.HIGHLIGHT),
          h: highlightFont.lineHeight,
          flexItem: {
            marginBottom: HIGHLIGHT_MARGIN_BOTTOM[screenSize],
          },
        },
        ExtraTitle: {
          type: SkeletonRectangle,
          w: getTextRectangleWidth(TEXT_WIDTH_RATIO.EXTRA_TITLE),
          h: extraTitleFont.lineHeight,
          flexItem: {
            marginBottom: EXTRA_TITLE_MARGIN_BOTTOM[screenSize],
          },
        },
        ExtraHighlightRow: {
          visible: isExtraHighlightRowVisible,
          flex: {
            direction: 'row',
            alignItems: 'center',
          },
          flexItem: {
            marginBottom: EXTRA_HIGHLIGHT_MARGIN_BOTTOM[screenSize],
          },
          ExtraHighlight: {
            type: SkeletonRectangle,
            visible: SHOW_EXTRA_HIGHLIGHT,
            w: getTextRectangleWidth(TEXT_WIDTH_RATIO.EXTRA_HIGHLIGHT),
            h: highlightFont.lineHeight,
            flexItem: {
              marginRight: EXTRA_HIGHLIGHT_MARGIN_RIGHT[screenSize],
            },
          },
          ContentAdvisory: {
            flex: {
              direction: 'row',
            },
            IconsRow: {
              visible: SHOW_CONTENT_ADVISORY,
              h: advisoryIconSize,
              flex: {
                direction: 'row',
              },
              children: [...Array(CONTENT_ADVISORY_ICONS_COUNT)].map(() => ({
                type: SkeletonRectangle,
                w: advisoryIconSize,
                h: advisoryIconSize,
                flexItem: {
                  marginRight: gap,
                },
              })),
            },
            ContentRatingIcon: {
              type: SkeletonRectangle,
              visible: isContentRatingIconVisible,
              w: basicIconSize,
              h: basicIconSize,
            },
          },
        },
        Description: {
          flex: {
            direction: 'column',
          },
          flexItem: {
            marginBottom: DESCRIPTION_MARGIN_BOTTOM[screenSize],
          },
          children: [...Array(descriptionLines)].map((_, index) => {
            const isLastLine = index === descriptionLines - 1;

            return {
              type: SkeletonRectangle,
              w: getTextRectangleWidth(
                isLastLine ? TEXT_WIDTH_RATIO.DESCRIPTION_LAST_LINE : TEXT_WIDTH_RATIO.DESCRIPTION,
              ),
              h: descriptionFont.lineHeight - gap,
              flexItem: {
                marginBottom: isLastLine ? 0 : gap,
              },
            };
          }),
        },
        Details: {
          type: SkeletonRectangle,
          visible: SHOW_DETAILS,
          w: getTextRectangleWidth(TEXT_WIDTH_RATIO.DETAILS),
          h: detailsFont.lineHeight,
          flexItem: {
            marginBottom: DETAILS_MARGIN_BOTTOM[screenSize],
          },
        },
      },
      ButtonsRow: {
        flex: {
          direction: 'row',
        },
        flexItem: {
          marginBottom: CTA_MARGIN_BOTTOM[screenSize],
        },
        children: BUTTON_WIDTHS[screenSize].map((buttonWidth, index, buttons) => ({
          type: SkeletonRectangle,
          w: buttonWidth,
          h: CTA_HEIGHT[screenSize],
          radius: CTA_BORDER_RADIUS[screenSize],
          flexItem: {
            marginRight: index === buttons.length - 1 ? 0 : CTA_BUTTON_MARGIN_RIGHT[screenSize],
          },
        })),
      },
      BottomRow: {
        visible: SHOW_BOTTOM_ROW,
        flex: {
          direction: 'row',
          alignItems: 'center',
        },
        ExtraDetails: {
          type: SkeletonRectangle,
          w: getTextRectangleWidth(TEXT_WIDTH_RATIO.EXTRA_DETAILS),
          h: extraDetailsFont.lineHeight,
          flexItem: {
            marginRight: BOTTOM_ROW_GAP[screenSize],
          },
        },
        ContentRatingIcon: {
          type: SkeletonRectangle,
          w: basicIconSize,
          h: basicIconSize,
        },
      },
    },
  };
}, cacheResolver);
